const SECRET = process.env.REACT_APP_STORAGE_SECRET || 'erc-secure-default-key-2024';
const SALT   = 'erc-storage-salt-v1';

// `language` is read synchronously by the inline <script> in public/index.html and
// by ~190 raw JSON.parse(localStorage.getItem('language')) call sites, none of which
// can decrypt. Encrypting it — which the legacy-plaintext upgrade below used to do
// on the first getItem('language') — makes every one of those readers throw and
// blanks the app. It stays plaintext JSON on both the read and write paths.
const PLAINTEXT_KEYS = new Set(['language']);

let _keyCache = null;

// In-memory mirror of decrypted values — allows synchronous reads after loadCache()
const _memCache = {};

// Outstanding encrypted writes — awaited by flushStorage() before a page unload
const _pendingWrites = new Set();

// Our ciphertext is always standard base64 (btoa output). Legacy plaintext —
// JSON objects ('{', '"') and raw JWT tokens ('.') — always contains characters
// outside the base64 alphabet, so anything that is NOT valid base64 is safe to
// treat as legacy plaintext and upgrade in place.
function looksEncrypted(value) {
  return /^[A-Za-z0-9+/]+={0,2}$/.test(value);
}

async function getDerivedKey() {
  if (_keyCache) return _keyCache;

  const encoder = new TextEncoder();

  const keyMaterial = await globalThis.crypto.subtle.importKey(
    'raw',
    encoder.encode(SECRET),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  _keyCache = await globalThis.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(SALT),
      iterations: 100_000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return _keyCache;
}

export function setItem(key, value) {
  // Update the in-memory mirror synchronously so getItemSync() reflects the
  // write immediately, even before the encrypted localStorage write resolves.
  _memCache[key] = value;

  if (PLAINTEXT_KEYS.has(key)) {
    localStorage.setItem(key, JSON.stringify(value));
    return Promise.resolve();
  }

  const write = (async () => {
    try {
      const cryptoKey = await getDerivedKey();
      const iv        = globalThis.crypto.getRandomValues(new Uint8Array(12));
      const encoded   = new TextEncoder().encode(JSON.stringify(value));

      const cipherBuf = await globalThis.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encoded
      );

      const packed = new Uint8Array(12 + cipherBuf.byteLength);
      packed.set(iv, 0);
      packed.set(new Uint8Array(cipherBuf), 12);

      localStorage.setItem(key, btoa(String.fromCodePoint(...packed)));
    } catch (err) {
      console.error('[secureStorage] setItem failed:', err);
    }
  })();

  _pendingWrites.add(write);
  write.finally(() => _pendingWrites.delete(write));
  return write;
}

/**
 * Resolves once every in-flight setItem() encryption+write has landed in
 * localStorage. Call `await flushStorage()` before any full-page unload
 * (window.location / window.open(_self) / reload) that follows a setItem,
 * otherwise the deferred encrypted write can be lost to the unload.
 */
export function flushStorage() {
  return Promise.all([..._pendingWrites]);
}

export async function getItem(key) {
  if (PLAINTEXT_KEYS.has(key)) {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const result = JSON.parse(raw);
      _memCache[key] = result;
      return result;
    } catch {
      // Ciphertext left behind by a build that ran this key through the encrypted
      // path — drop it instead of handing back a value the raw readers can't parse.
      localStorage.removeItem(key);
      return null;
    }
  }

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const packed    = Uint8Array.from(atob(stored), c => c.codePointAt(0));
    const iv        = packed.slice(0, 12);
    const cipher    = packed.slice(12);
    const cryptoKey = await getDerivedKey();

    const plainBuf = await globalThis.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      cipher
    );

    const result = JSON.parse(new TextDecoder().decode(plainBuf));
    _memCache[key] = result;
    return result;
  } catch (decryptErr) {
    // Value was stored unencrypted by a prior deployment — fall back to plain JSON,
    // then to the raw string (legacy tokens like access_token were never stringified).
    console.warn('[secureStorage] decryption failed, attempting plaintext fallback:', decryptErr);
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    let result;
    try {
      result = JSON.parse(raw);
    } catch {
      result = raw;
    }
    _memCache[key] = result;
    // Upgrade legacy plaintext to the encrypted format in place, so a single page
    // load after deployment migrates an existing session without a re-login.
    // Skip values that are valid base64 — those are genuine ciphertext that failed
    // to decrypt (e.g. a rotated secret) and must be left intact, not overwritten.
    if (!looksEncrypted(raw)) {
      setItem(key, result);
    }
    return result;
  }
}

/**
 * Synchronous read from the in-memory cache.
 * Returns null if loadCache() has not been called yet for this key.
 */
export function getItemSync(key) {
  return _memCache[key] ?? null;
}

/**
 * Decrypt all given keys into the in-memory cache.
 * Call this once at app startup before ReactDOM.render so that
 * getItemSync() is always warm when components first render.
 */
export async function loadCache(keys) {
  await Promise.all(keys.map(k => getItem(k)));
}

export function removeItem(key) {
  delete _memCache[key];
  localStorage.removeItem(key);
}

export function clearAll() {
  Object.keys(_memCache).forEach(k => delete _memCache[k]);
  localStorage.clear();
}
